import { useState, useEffect } from 'react';

/**
 * Avatar with graceful fallback to colored initials when the image is missing or fails to load.
 *
 * Props:
 *  - src: optional URL (relative API path or absolute)
 *  - name: full name; used for initials + deterministic color
 *  - size: pixel size (default 40)
 *  - className: extra wrapper classes (e.g. ring, border)
 *  - testId: data-testid for the wrapper
 */
const COLORS = [
  'bg-gradient-to-br from-[#ccff00] to-[#88aa00] text-black',
  'bg-gradient-to-br from-[#00f0ff] to-[#0088aa] text-black',
  'bg-gradient-to-br from-[#ff4d00] to-[#ffaa00] text-black',
  'bg-gradient-to-br from-[#a855f7] to-[#6b21a8] text-white',
  'bg-gradient-to-br from-[#f472b6] to-[#be185d] text-white',
  'bg-gradient-to-br from-[#22d3ee] to-[#0e7490] text-black',
];

const getInitials = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const colorFor = (name) => {
  if (!name) return COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) & 0xffffffff;
  return COLORS[Math.abs(hash) % COLORS.length];
};

const buildUrl = (src) => {
  if (!src) return null;
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:')) return src;
  return `${process.env.REACT_APP_BACKEND_URL}${src.startsWith('/') ? '' : '/'}${src}`;
};

const Avatar = ({ src, name, size = 40, className = '', testId }) => {
  const [errored, setErrored] = useState(false);

  // If src changes (e.g. user uploads new photo), reset error
  useEffect(() => {
    setErrored(false);
  }, [src]);

  const finalUrl = !errored ? buildUrl(src) : null;
  const initials = getInitials(name);
  const colorClass = colorFor(name);
  const fontSize = Math.max(10, Math.floor(size * 0.4));

  return (
    <div
      className={`rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 ${className}`}
      style={{ width: size, height: size }}
      data-testid={testId}
    >
      {finalUrl ? (
        <img
          src={finalUrl}
          alt={name || 'avatar'}
          className="w-full h-full object-cover"
          onError={() => setErrored(true)}
          loading="lazy"
        />
      ) : (
        <div
          className={`w-full h-full flex items-center justify-center font-black ${colorClass}`}
          style={{ fontSize }}
        >
          {initials}
        </div>
      )}
    </div>
  );
};

export default Avatar;
